import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

const CANDIDATE_URLS = [
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/macro_dimension_scores.json",
  "https://cdn.jsdelivr.net/gh/aport550/majorah-data@main/public/data/macro_dimension_scores.json",
];

const DAILY_RETURNS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/daily_returns.csv";

const STOCK_SCREENER_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

const RETURN_TICKERS = ["SPY", "FBTC", "GLD", "UUP", "DBC", "XOP", "TLT"];
const TOP_BOTTOM_MIN_MARKET_CAP = 40_000_000_000;

function useIsMobile(breakpoint = 760) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}

function parseNumber(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const cleaned = s.replace(/[$,%]/g, "").replace(/,/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function normalizeTicker(v) {
  return String(v || "").trim().toUpperCase();
}

function formatNumber(v, digits = 2) {
  if (!isFiniteNumber(v)) return "—";
  return v.toFixed(digits);
}

function formatPercent(v, digits = 2) {
  if (!isFiniteNumber(v)) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}

function formatLargeMoney(n) {
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  const abs = Math.abs(value);

  if (abs >= 1_000_000_000_000) return `$${(value / 1_000_000_000_000).toFixed(1)}T`;
  if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(0)}B`;
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(0)}M`;
  return `$${value.toFixed(0)}`;
}

function getScoreStyle(v) {
  if (!isFiniteNumber(v)) return {};
  if (v > 1.5) return { color: "#047857", fontWeight: 900 };
  if (v > 0.5) return { color: "#16a34a", fontWeight: 800 };
  if (v < -1.5) return { color: "#b91c1c", fontWeight: 900 };
  if (v < -0.5) return { color: "#dc2626", fontWeight: 800 };
  return { color: "#334155" };
}

// function getReturnStyle(v) {
//   if (!isFiniteNumber(v)) return {};
//   if (v > 0) return { color: "#15803d", fontWeight: 900 };
//   if (v < 0) return { color: "#b91c1c", fontWeight: 900 };
//   return { color: "#334155" };
// }

// function getReturnCardStyle(v) {
//   if (!isFiniteNumber(v)) {
//     return {
//       background: "#f8fafc",
//       borderColor: "#e2e8f0",
//       color: "#475569",
//     };
//   }

//   if (v > 0) {
//     return {
//       background: "linear-gradient(135deg, #ecfdf5 0%, #dcfce7 100%)",
//       borderColor: "#bbf7d0",
//       color: "#14532d",
//     };
//   }

//   return {
//     background: "linear-gradient(135deg, #fff1f2 0%, #fee2e2 100%)",
//     borderColor: "#fecdd3",
//     color: "#7f1d1d",
//   };
// }

async function fetchJsonFromCandidates(urls) {
  let lastError = null;

  for (const url of urls) {
    try {
      const res = await fetch(url, { cache: "no-store" });

      if (!res.ok) {
        lastError = new Error(`HTTP ${res.status} from ${url}`);
        continue;
      }

      const text = await res.text();
      const trimmed = text.trim();

      if (!trimmed) {
        lastError = new Error(`Empty response from ${url}`);
        continue;
      }

      if (
        trimmed.startsWith("<!DOCTYPE") ||
        trimmed.startsWith("<html") ||
        trimmed.startsWith("<")
      ) {
        lastError = new Error(`Expected JSON but received HTML from ${url}`);
        continue;
      }

      const json = JSON.parse(text);
      return { json, url };
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("Unable to load macro_dimension_scores.json");
}

async function fetchText(url) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} from ${url}`);
  }
  return res.text();
}

function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    const next = line[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }

  out.push(cur);
  return out;
}

function normalizeDateString(value) {
  const s = String(value || "").trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return s;

  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}


function getWeekdayFromDate(dateString) {
  const s = String(dateString || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "—";

  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(dt.getTime())) return "—";

  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dt.getUTCDay()];
}

function parseDailyReturnsCsv(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);

  if (!lines.length) {
    return {
      returnsByDate: new Map(),
      allReturnsByDate: new Map(),
      allTickers: [],
    };
  }

  const header = parseCsvLine(lines[0]);

  let dateIdx = header.findIndex(
    (h) => String(h).trim().toLowerCase() === "date"
  );

  if (dateIdx === -1 && header.length > 0) {
    const firstHeader = String(header[0] || "").trim().toLowerCase();
    if (firstHeader === "" || firstHeader === "unnamed: 0") {
      dateIdx = 0;
    }
  }

  if (dateIdx === -1) {
    throw new Error("daily_returns.csv must contain a date column.");
  }

  const idxMap = {};
  RETURN_TICKERS.forEach((ticker) => {
    idxMap[ticker] = header.findIndex(
      (h) => String(h).trim().toUpperCase() === ticker
    );
  });

  const allTickers = header
    .map((h, idx) => ({ ticker: normalizeTicker(h), idx }))
    .filter((x) => x.idx !== dateIdx && x.ticker);

  const returnsByDate = new Map();
  const allReturnsByDate = new Map();

  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    const date = normalizeDateString(cols[dateIdx]);

    if (!date) continue;

    const out = {};

    RETURN_TICKERS.forEach((ticker) => {
      const idx = idxMap[ticker];
      if (idx === -1) {
        out[ticker] = null;
        return;
      }

      const raw = cols[idx] === "" ? null : Number(cols[idx]);
      out[ticker] = Number.isFinite(raw) ? raw / 100 : null;
    });

    const allOut = {};
    for (const { ticker, idx } of allTickers) {
      const raw = cols[idx] === "" ? null : Number(cols[idx]);
      allOut[ticker] = Number.isFinite(raw) ? raw / 100 : null;
    }

    returnsByDate.set(date, out);
    allReturnsByDate.set(date, allOut);
  }

  return { returnsByDate, allReturnsByDate, allTickers: allTickers.map((x) => x.ticker) };
}

function averageFinite(values) {
  const clean = values.filter(isFiniteNumber);
  if (!clean.length) return null;
  return clean.reduce((sum, x) => sum + x, 0) / clean.length;
}

function getAverageAnchorLevel(anchorLevels, tickers) {
  const values = tickers
    .map((ticker) => anchorLevels.get(normalizeTicker(ticker)))
    .filter(isFiniteNumber);

  if (!values.length) return null;
  return values.reduce((sum, x) => sum + x, 0) / values.length;
}

function centerMacroLevels(rows, key, outputKey) {
  const avg = averageFinite(rows.map((row) => row[key]));

  return rows.map((row) => ({
    ...row,
    [outputKey]: isFiniteNumber(row[key]) && isFiniteNumber(avg) ? row[key] - avg : null,
  }));
}

function addMacroLevelIndexes(rows, allReturnsByDate) {
  const chronological = [...(rows || [])]
    .filter((row) => row?.date)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));

  const anchorTickers = [
    "GLD",
    "XLE",
    "XOP",
    "DBC",
    "TLT",
    "QQQ",
    "SPY",
    "IWM",
    "PSQ",
    "SH",
    "HYG",
    "JNK",
    "UUP",
  ];

  const anchorLevels = new Map(anchorTickers.map((ticker) => [ticker, 100]));

  const withRawLevels = chronological.map((row) => {
    const returns = allReturnsByDate.get(row.date) || {};

    for (const ticker of anchorTickers) {
      const dailyReturn = returns[ticker];
      const prevLevel = anchorLevels.get(ticker);

      if (isFiniteNumber(prevLevel) && isFiniteNumber(dailyReturn)) {
        anchorLevels.set(ticker, prevLevel * (1 + dailyReturn));
      }
    }

    const inflationUp = getAverageAnchorLevel(anchorLevels, ["GLD", "XLE", "XOP", "DBC"]);
    const inflationDown = getAverageAnchorLevel(anchorLevels, ["TLT"]);

    const growthUp = getAverageAnchorLevel(anchorLevels, ["QQQ", "SPY", "IWM"]);
    const growthDown = getAverageAnchorLevel(anchorLevels, ["PSQ", "SH"]);

    const liquidityUp = getAverageAnchorLevel(anchorLevels, ["HYG", "JNK"]);
    const liquidityDown = getAverageAnchorLevel(anchorLevels, ["UUP"]);

    return {
      ...row,
      raw_inflation_level:
        isFiniteNumber(inflationUp) && isFiniteNumber(inflationDown)
          ? inflationUp - inflationDown
          : null,
      raw_growth_level:
        isFiniteNumber(growthUp) && isFiniteNumber(growthDown)
          ? growthUp - growthDown
          : null,
      raw_liquidity_level:
        isFiniteNumber(liquidityUp) && isFiniteNumber(liquidityDown)
          ? liquidityUp - liquidityDown
          : null,
    };
  });

  const centeredInflation = centerMacroLevels(
    withRawLevels,
    "raw_inflation_level",
    "inflation_level"
  );

  const centeredGrowth = centerMacroLevels(
    centeredInflation,
    "raw_growth_level",
    "growth_level"
  );

  const centeredLiquidity = centerMacroLevels(
    centeredGrowth,
    "raw_liquidity_level",
    "liquidity_level"
  );

  const byDate = new Map(centeredLiquidity.map((row) => [row.date, row]));

  return rows.map((row) => byDate.get(row.date) || row);
}

function normalizeScreenerPayload(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.rows)) return json.rows;
  return [];
}

async function fetchScreenerMap() {
  const res = await fetch(STOCK_SCREENER_URL, { cache: "no-store" });
  const text = await res.text();

  if (!res.ok || text.trim().startsWith("<")) {
    throw new Error(`Unable to load stock_screener.json. First 120 chars: ${text.slice(0, 120)}`);
  }

  const rows = normalizeScreenerPayload(JSON.parse(text));
  const map = new Map();

  for (const row of rows) {
    const ticker = normalizeTicker(row?.ticker ?? row?.Ticker ?? row?.symbol ?? row?.Symbol);
    if (!ticker) continue;

    map.set(ticker, {
      ticker,
      name: String(row?.name ?? row?.stock ?? row?.company ?? "").trim(),
      marketCap: parseNumber(row?.marketCap ?? row?.market_cap ?? row?.MarketCap),
      price: parseNumber(row?.price ?? row?.currentPrice),
    });
  }

  return map;
}

function getTopBottomStocks(allReturnsByDate, screenerMap, date) {
  const returns = allReturnsByDate.get(date);
  if (!returns) return { top: null, bottom: null };

  const eligible = Object.entries(returns)
    .map(([ticker, dailyReturn]) => {
      const meta = screenerMap.get(normalizeTicker(ticker));
      return {
        ticker: normalizeTicker(ticker),
        dailyReturn,
        marketCap: meta?.marketCap ?? null,
        name: meta?.name ?? "",
        price: meta?.price ?? null,
      };
    })
    .filter(
      (x) =>
        x.ticker &&
        isFiniteNumber(x.dailyReturn) &&
        isFiniteNumber(x.marketCap) &&
        x.marketCap >= TOP_BOTTOM_MIN_MARKET_CAP
    );

  if (!eligible.length) return { top: null, bottom: null };

  eligible.sort((a, b) => b.dailyReturn - a.dailyReturn);

  return {
    top: eligible[0],
    bottom: eligible[eligible.length - 1],
  };
}

function getSignLabel(v) {
  if (!isFiniteNumber(v)) return null;
  return v >= 0 ? "pos" : "neg";
}

function getMacroRegime(row) {
  const inf = getSignLabel(row?.inflation_score);
  const gro = getSignLabel(row?.growth_score);
  const liq = getSignLabel(row?.liquidity_score);

  if (!inf || !gro || !liq) return "—";

  const key = `${inf}_${gro}_${liq}`;

  const regimeMap = {
    pos_pos_pos: "Inflationary Growth + Easy Liquidity",
    pos_pos_neg: "Inflationary Growth",
    pos_neg_pos: "Stagflation + Easy Liquidity",
    pos_neg_neg: "Stagflation",
    neg_pos_pos: "Deflationary Growth",
    neg_pos_neg: "Deflationary Growth + Tight Liquidity",
    neg_neg_pos: "Deflationary Recession",
    neg_neg_neg: "Deflationary Recession + Tight Liquidity",
  };

  return regimeMap[key] || "—";
}

function getRegimeStyle(regime) {
  if (!regime || regime === "—") return { color: "#6b7280" };

  if (regime.toLowerCase().includes("stagflation")) {
    return {
      color: "#b45309",
      background: "#fffbeb",
      fontWeight: 800,
      border: "1px solid #fde68a",
      borderRadius: 9999,
      padding: "5px 10px",
      display: "inline-flex",
      alignItems: "center",
    };
  }

  if (regime.toLowerCase().includes("deflationary recession")) {
    return {
      color: "#1d4ed8",
      background: "#eff6ff",
      fontWeight: 800,
      border: "1px solid #bfdbfe",
      borderRadius: 9999,
      padding: "5px 10px",
      display: "inline-flex",
      alignItems: "center",
    };
  }

  if (regime.toLowerCase().includes("deflationary growth")) {
    return {
      color: "#047857",
      background: "#ecfdf5",
      fontWeight: 800,
      border: "1px solid #a7f3d0",
      borderRadius: 9999,
      padding: "5px 10px",
      display: "inline-flex",
      alignItems: "center",
    };
  }

  if (regime.toLowerCase().includes("inflationary growth")) {
    return {
      color: "#7c3aed",
      background: "#f5f3ff",
      fontWeight: 800,
      border: "1px solid #ddd6fe",
      borderRadius: 9999,
      padding: "5px 10px",
      display: "inline-flex",
      alignItems: "center",
    };
  }

  return {
    color: "#374151",
    background: "#f3f4f6",
    fontWeight: 700,
    border: "1px solid #e5e7eb",
    borderRadius: 9999,
    padding: "5px 10px",
    display: "inline-flex",
    alignItems: "center",
  };
}

function compareValues(a, b, direction = "asc") {
  const dir = direction === "asc" ? 1 : -1;

  const aMissing =
    a === null || a === undefined || a === "" || Number.isNaN(a);
  const bMissing =
    b === null || b === undefined || b === "" || Number.isNaN(b);

  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  if (typeof a === "number" && typeof b === "number") {
    return (a - b) * dir;
  }

  const aDate =
    typeof a === "string" && /^\d{4}-\d{2}-\d{2}$/.test(a)
      ? new Date(a).getTime()
      : null;
  const bDate =
    typeof b === "string" && /^\d{4}-\d{2}-\d{2}$/.test(b)
      ? new Date(b).getTime()
      : null;

  if (Number.isFinite(aDate) && Number.isFinite(bDate)) {
    return (aDate - bDate) * dir;
  }

  return String(a).localeCompare(String(b)) * dir;
}

function getSortValue(row, key) {
  switch (key) {
    case "date":
      return row.date || "";
    case "weekday":
      return row.weekday || "";
    case "inflation_score":
      return row.inflation_score;
    case "growth_score":
      return row.growth_score;
    case "liquidity_score":
      return row.liquidity_score;
    case "inflation_level":
      return row.inflation_level;
    case "growth_level":
      return row.growth_level;
    case "liquidity_level":
      return row.liquidity_level;
    case "spy_daily_return":
      return row.spy_daily_return;
    case "fbtc_return":
      return row.fbtc_return;
    case "gld_return":
      return row.gld_return;
    case "uup_return":
      return row.uup_return;
    case "dbc_return":
      return row.dbc_return;
    case "xop_return":
      return row.xop_return;
    case "tlt_return":
      return row.tlt_return;
    case "top_stock_return":
      return row.top_stock?.dailyReturn;
    case "bottom_stock_return":
      return row.bottom_stock?.dailyReturn;
    case "macro_regime":
      return getMacroRegime(row);
    case "inflation_score_ema5":
      return row.inflation_score_ema5;
    case "growth_score_ema5":
      return row.growth_score_ema5;
    case "liquidity_score_ema5":
      return row.liquidity_score_ema5;
    case "inflation_pct_rank":
      return row.inflation_pct_rank;
    case "growth_pct_rank":
      return row.growth_pct_rank;
    case "liquidity_pct_rank":
      return row.liquidity_pct_rank;
    default:
      return row[key];
  }
}

function ReturnPill({ value }) {
  return (
    <span
      style={{
        ...returnPill,
        ...(isFiniteNumber(value)
          ? value >= 0
            ? positiveReturnPill
            : negativeReturnPill
          : neutralReturnPill),
      }}
    >
      {formatPercent(value, 2)}
    </span>
  );
}

// function StockMoverCard({ title, stock, tone }) {
//   const style = getReturnCardStyle(stock?.dailyReturn);

//   return (
//     <div
//       style={{
//         border: `1px solid ${style.borderColor}`,
//         background: style.background,
//         color: style.color,
//         borderRadius: 22,
//         padding: 16,
//         boxShadow: "0 14px 34px rgba(15,23,42,0.08)",
//         minHeight: 132,
//       }}
//     >
//       <div
//         style={{
//           fontSize: 11,
//           fontWeight: 950,
//           letterSpacing: "0.1em",
//           textTransform: "uppercase",
//           opacity: 0.82,
//           marginBottom: 10,
//         }}
//       >
//         {title}
//       </div>

//       {stock ? (
//         <>
//           <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
//             <div style={{ fontSize: 30, fontWeight: 950, letterSpacing: "-0.06em" }}>
//               {stock.ticker}
//             </div>
//             <div style={{ fontSize: 24, fontWeight: 950, letterSpacing: "-0.05em" }}>
//               {formatPercent(stock.dailyReturn, 2)}
//             </div>
//           </div>
//           <div style={{ marginTop: 4, color: "rgba(15,23,42,0.72)", fontSize: 13, fontWeight: 700 }}>
//             {stock.name || "—"}
//           </div>
//           <div style={{ marginTop: 12, color: "rgba(15,23,42,0.62)", fontSize: 12, fontWeight: 800 }}>
//             Market cap {formatLargeMoney(stock.marketCap)}
//           </div>
//         </>
//       ) : (
//         <div style={{ color: "#64748b", fontWeight: 800 }}>No eligible stock found.</div>
//       )}
//     </div>
//   );
// }


// function getWeekKey(dateString) {
//   const s = String(dateString || "").trim();
//   if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";

//   const [y, m, d] = s.split("-").map(Number);
//   const date = new Date(Date.UTC(y, m - 1, d));
//   if (Number.isNaN(date.getTime())) return "";

//   const day = date.getUTCDay() || 7;
//   date.setUTCDate(date.getUTCDate() + 4 - day);

//   const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
//   const weekNo = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
//   return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
// }

function getWeeklyTrendRows(rows) {
  return (rows || [])
    .filter((row) => row?.date)
    .slice()
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .map((row) => ({
      date: row.date,
      label: row.date,
      inflationLevel: row.inflation_level,
      growthLevel: row.growth_level,
      liquidityLevel: row.liquidity_level,
    }));
}

function WeeklyTrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        padding: 12,
        boxShadow: "0 14px 34px rgba(15,23,42,0.16)",
        fontSize: 12,
        color: "#0f172a",
      }}
    >
      <div style={{ fontWeight: 950, marginBottom: 6 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color, fontWeight: 850, marginTop: 3 }}>
          {p.name}: {formatNumber(p.value, 2)}
        </div>
      ))}
    </div>
  );
}

function WeeklyTrendChart({ data, isMobile }) {
  if (!data?.length) {
    return (
      <div style={messageCard}>
        No macro level data available.
      </div>
    );
  }

  return (
    <section style={{ ...tableCard, marginTop: 18 }}>
      <div style={chartHeader}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 950, color: "#0f172a" }}>
            Indexed Macro Levels
          </div>
          <div style={{ color: "#64748b", marginTop: 4, fontSize: 13 }}>
            Lines show the same Inflation Level, Growth Level, and Liquidity Level columns from the table. 0 = average indexed level across the full dataset.
          </div>
        </div>
      </div>

      <div style={{ width: "100%", height: isMobile ? 320 : 420 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{
              top: 16,
              right: isMobile ? 10 : 26,
              bottom: isMobile ? 18 : 24,
              left: isMobile ? -18 : 8,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: isMobile ? 9 : 11, fill: "#64748b" }}
              interval={isMobile ? Math.max(1, Math.floor(data.length / 6)) : Math.max(0, Math.floor(data.length / 12))}
            />

            <YAxis
              tick={{ fontSize: isMobile ? 9 : 11, fill: "#64748b" }}
              tickFormatter={(v) => formatNumber(Number(v), 0)}
              domain={["auto", "auto"]}
            />

            <Tooltip content={<WeeklyTrendTooltip />} />
            <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />

            <Line
              type="monotone"
              dataKey="inflationLevel"
              name="Inflation Level"
              stroke="#dc2626"
              strokeWidth={2.5}
              dot={false}
              connectNulls
            />

            <Line
              type="monotone"
              dataKey="growthLevel"
              name="Growth Level"
              stroke="#2563eb"
              strokeWidth={2.5}
              dot={false}
              connectNulls
            />

            <Line
              type="monotone"
              dataKey="liquidityLevel"
              name="Liquidity Level"
              stroke="#16a34a"
              strokeWidth={2.5}
              dot={false}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}


export default function MacroDimensionTable() {
  const isMobile = useIsMobile(760);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loadedUrl, setLoadedUrl] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [showEma, setShowEma] = useState(true);
  const [showPctRank, setShowPctRank] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  useEffect(() => {
    let ignore = false;

    async function loadData() {
      try {
        setLoading(true);
        setError("");

        const [{ json, url }, dailyReturnsText, screenerMap] = await Promise.all([
          fetchJsonFromCandidates(CANDIDATE_URLS),
          fetchText(DAILY_RETURNS_URL),
          fetchScreenerMap(),
        ]);

        if (!Array.isArray(json)) {
          throw new Error(`Macro dimension JSON from ${url} is not an array.`);
        }

        const { returnsByDate, allReturnsByDate } = parseDailyReturnsCsv(dailyReturnsText);

        const merged = json.map((row) => {
          const normalizedDate = normalizeDateString(row?.date);
          const returns = returnsByDate.get(normalizedDate) || {};
          const movers = getTopBottomStocks(allReturnsByDate, screenerMap, normalizedDate);

          return {
            ...row,
            date: normalizedDate || row?.date || "",
            weekday: getWeekdayFromDate(normalizedDate),
            spy_daily_return:
              returns.SPY ?? row?.spy_daily_return ?? row?.spy_return ?? null,
            fbtc_return: returns.FBTC ?? row?.fbtc_return ?? null,
            gld_return: returns.GLD ?? row?.gld_return ?? null,
            uup_return: returns.UUP ?? row?.uup_return ?? null,
            dbc_return: returns.DBC ?? row?.dbc_return ?? null,
            xop_return: returns.XOP ?? row?.xop_return ?? null,
            tlt_return: returns.TLT ?? row?.tlt_return ?? null,
            top_stock: movers.top,
            bottom_stock: movers.bottom,
          };
        });

        const mergedWithMacroLevels = addMacroLevelIndexes(merged, allReturnsByDate);

        if (!ignore) {
          setRows(mergedWithMacroLevels);
          setLoadedUrl(url);
        }
      } catch (err) {
        if (!ignore) {
          setError(err?.message || "Failed to load macro dimension scores.");
          setRows([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      ignore = true;
    };
  }, []);

  const hasEmaColumns = useMemo(() => {
    return rows.some(
      (r) =>
        "inflation_score_ema5" in r ||
        "growth_score_ema5" in r ||
        "liquidity_score_ema5" in r
    );
  }, [rows]);

  const hasPctRankColumns = useMemo(() => {
    return rows.some(
      (r) =>
        "inflation_pct_rank" in r ||
        "growth_pct_rank" in r ||
        "liquidity_pct_rank" in r
    );
  }, [rows]);

  const filteredRows = useMemo(() => {
    let out = rows;

    if (dateFilter.trim()) {
      const q = dateFilter.trim().toLowerCase();
      out = out.filter((r) => String(r.date || "").toLowerCase().includes(q));
    }

    const sorted = [...out].sort((a, b) => {
      const av = getSortValue(a, sortConfig.key);
      const bv = getSortValue(b, sortConfig.key);
      return compareValues(av, bv, sortConfig.direction);
    });

    return sorted;
  }, [rows, dateFilter, sortConfig]);

  // const latestRow = useMemo(() => {
  //   return [...rows]
  //     .filter((r) => r.date)
  //     .sort((a, b) => compareValues(a.date, b.date, "desc"))[0];
  // }, [rows]);

  //const latestRegime = latestRow ? getMacroRegime(latestRow) : "—";

  const weeklyTrendRows = useMemo(() => {
    return getWeeklyTrendRows(rows);
  }, [rows]);

  function toggleSort(key) {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: key === "date" ? "desc" : "asc",
      };
    });
  }

  function renderSortArrow(key) {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  }

  function SortableHeader({ label, sortKey, sticky = false }) {
    return (
      <th
        onClick={() => toggleSort(sortKey)}
        style={{
          ...thStyle,
          ...(sticky ? stickyThStyle : {}),
          cursor: "pointer",
          userSelect: "none",
        }}
        title={`Sort by ${label}`}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span>{label}</span>
          <span
            style={{
              fontSize: 11,
              color: sortConfig.key === sortKey ? "#111827" : "#9ca3af",
            }}
          >
            {renderSortArrow(sortKey)}
          </span>
        </span>
      </th>
    );
  }

  return (
    <div style={{ ...pageShell, padding: isMobile ? 8 : 16 }}>
      <div style={{ maxWidth: 1680, width: "100%", margin: "0 auto" }}>
        <section style={{ ...heroCard, padding: isMobile ? 16 : 24, borderRadius: isMobile ? 20 : 28 }}>
          <div style={eyebrow}>Majorah Macro Tape</div>
          <h1 style={{ ...heroTitle, fontSize: isMobile ? 20 : 22 }}>Daily Returns</h1>
          <p style={heroSubtitle}>
          </p>

          <div style={heroStatsGrid}>
{/*            <HeroMetric label="Latest Date" value={latestRow?.date || "—"} />
            <HeroMetric label="Current Regime" value={latestRegime} />
            <HeroMetric label="SPY" value={formatPercent(latestRow?.spy_daily_return, 2)} valueStyle={getReturnStyle(latestRow?.spy_daily_return)} />
            <HeroMetric label="Rows" value={rows.length.toLocaleString()} />
*/}          </div>
        </section>

        {loading && (
          <div style={messageCard}>
            Loading macro scores...
          </div>
        )}

        {!loading && error && (
          <div style={errorCard}>
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
{/*            <section style={moversGrid}>
              <StockMoverCard
                title="Top Large-Cap Stock Today"
                stock={latestRow?.top_stock}
                tone="positive"
              />
              <StockMoverCard
                title="Bottom Large-Cap Stock Today"
                stock={latestRow?.bottom_stock}
                tone="negative"
              />
            </section>
*/}
            <section style={{ ...controlsCard, padding: isMobile ? 12 : 16, borderRadius: isMobile ? 18 : 22 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 950, color: "#0f172a" }}>
                  Daily Return Table
                </div>
                <div style={{ color: "#64748b", marginTop: 4, fontSize: 13 }}>
                  Top/bottom stocks require market cap above {formatLargeMoney(TOP_BOTTOM_MIN_MARKET_CAP)}.
                </div>
                {loadedUrl && (
                  <div style={{ color: "#94a3b8", marginTop: 4, fontSize: 11 }}>
                  </div>
                )}
              </div>

              <div style={controlsRight}>
                <input
                  type="text"
                  placeholder="Filter date, e.g. 2025-01"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  style={searchInput}
                />

                {hasEmaColumns && (
                  <label style={toggleLabel}>
                    <input
                      type="checkbox"
                      checked={showEma}
                      onChange={(e) => setShowEma(e.target.checked)}
                    />
                    EMA
                  </label>
                )}

                {hasPctRankColumns && (
                  <label style={toggleLabel}>
                    <input
                      type="checkbox"
                      checked={showPctRank}
                      onChange={(e) => setShowPctRank(e.target.checked)}
                    />
                    Percentile Rank
                  </label>
                )}

                <div style={resultPill}>
                  Rows <strong>{filteredRows.length.toLocaleString()}</strong>
                </div>
              </div>
            </section>

            <section style={{ ...tableCard, padding: isMobile ? 8 : 18, borderRadius: isMobile ? 18 : 24 }}>
              <div
                style={{
                  overflowX: "auto",
                  overflowY: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: 18,
                  maxHeight: isMobile ? "72vh" : "76vh",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <table style={{ ...tableStyle, minWidth: isMobile ? 1480 : 1980 }}>
                  <thead>
                    <tr>
                      <SortableHeader label="Date" sortKey="date" sticky />
                      <SortableHeader label="Day" sortKey="weekday" />
                      <SortableHeader label="Inflation" sortKey="inflation_score" />
                      <SortableHeader label="Growth" sortKey="growth_score" />
                      <SortableHeader label="Liquidity" sortKey="liquidity_score" />
                      <SortableHeader label="Inflation Level" sortKey="inflation_level" />
                      <SortableHeader label="Growth Level" sortKey="growth_level" />
                      <SortableHeader label="Liquidity Level" sortKey="liquidity_level" />
                      <SortableHeader label="SPY" sortKey="spy_daily_return" />
                      <SortableHeader label="Top $40B+ Stock" sortKey="top_stock_return" />
                      <SortableHeader label="Bottom $40B+ Stock" sortKey="bottom_stock_return" />
                      <SortableHeader label="FBTC" sortKey="fbtc_return" />
                      <SortableHeader label="GLD" sortKey="gld_return" />
                      <SortableHeader label="UUP" sortKey="uup_return" />
                      <SortableHeader label="DBC" sortKey="dbc_return" />
                      <SortableHeader label="XOP" sortKey="xop_return" />
                      <SortableHeader label="TLT" sortKey="tlt_return" />
                      <SortableHeader label="Macro Regime" sortKey="macro_regime" />

                      {hasEmaColumns && showEma && (
                        <>
                          <SortableHeader label="Inflation EMA" sortKey="inflation_score_ema5" />
                          <SortableHeader label="Growth EMA" sortKey="growth_score_ema5" />
                          <SortableHeader label="Liquidity EMA" sortKey="liquidity_score_ema5" />
                        </>
                      )}

                      {hasPctRankColumns && showPctRank && (
                        <>
                          <SortableHeader label="Inflation % Rank" sortKey="inflation_pct_rank" />
                          <SortableHeader label="Growth % Rank" sortKey="growth_pct_rank" />
                          <SortableHeader label="Liquidity % Rank" sortKey="liquidity_pct_rank" />
                        </>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRows.map((row, idx) => {
                      const regime = getMacroRegime(row);
                      const rowBg = idx % 2 === 0 ? "#ffffff" : "#fbfdff";

                      return (
                        <tr key={row.date} style={{ background: rowBg }}>
                          <td style={{ ...tdStyle, ...stickyTdStyle }}>{row.date || "—"}</td>

                          <td style={tdStyle}>{row.weekday || "—"}</td>

                          <td style={{ ...tdStyle, ...getScoreStyle(row.inflation_score) }}>
                            {formatNumber(row.inflation_score)}
                          </td>

                          <td style={{ ...tdStyle, ...getScoreStyle(row.growth_score) }}>
                            {formatNumber(row.growth_score)}
                          </td>

                          <td style={{ ...tdStyle, ...getScoreStyle(row.liquidity_score) }}>
                            {formatNumber(row.liquidity_score)}
                          </td>

                          <td style={{ ...tdStyle, ...getScoreStyle(row.inflation_level) }}>
                            {formatNumber(row.inflation_level)}
                          </td>

                          <td style={{ ...tdStyle, ...getScoreStyle(row.growth_level) }}>
                            {formatNumber(row.growth_level)}
                          </td>

                          <td style={{ ...tdStyle, ...getScoreStyle(row.liquidity_level) }}>
                            {formatNumber(row.liquidity_level)}
                          </td>

                          <td style={tdStyle}>
                            <ReturnPill value={row.spy_daily_return} />
                          </td>

                          <td style={tdStyle}>
                            <StockMoveInline stock={row.top_stock} />
                          </td>

                          <td style={tdStyle}>
                            <StockMoveInline stock={row.bottom_stock} />
                          </td>

                          <td style={tdStyle}><ReturnPill value={row.fbtc_return} /></td>
                          <td style={tdStyle}><ReturnPill value={row.gld_return} /></td>
                          <td style={tdStyle}><ReturnPill value={row.uup_return} /></td>
                          <td style={tdStyle}><ReturnPill value={row.dbc_return} /></td>
                          <td style={tdStyle}><ReturnPill value={row.xop_return} /></td>
                          <td style={tdStyle}><ReturnPill value={row.tlt_return} /></td>

                          <td style={tdStyle}>
                            <span style={getRegimeStyle(regime)}>{regime}</span>
                          </td>

                          {hasEmaColumns && showEma && (
                            <>
                              <td style={{ ...tdStyle, ...getScoreStyle(row.inflation_score_ema5) }}>
                                {formatNumber(row.inflation_score_ema5)}
                              </td>

                              <td style={{ ...tdStyle, ...getScoreStyle(row.growth_score_ema5) }}>
                                {formatNumber(row.growth_score_ema5)}
                              </td>

                              <td style={{ ...tdStyle, ...getScoreStyle(row.liquidity_score_ema5) }}>
                                {formatNumber(row.liquidity_score_ema5)}
                              </td>
                            </>
                          )}

                          {hasPctRankColumns && showPctRank && (
                            <>
                              <td style={tdStyle}>
                                {isFiniteNumber(row.inflation_pct_rank)
                                  ? `${(row.inflation_pct_rank * 100).toFixed(1)}%`
                                  : "—"}
                              </td>

                              <td style={tdStyle}>
                                {isFiniteNumber(row.growth_pct_rank)
                                  ? `${(row.growth_pct_rank * 100).toFixed(1)}%`
                                  : "—"}
                              </td>

                              <td style={tdStyle}>
                                {isFiniteNumber(row.liquidity_pct_rank)
                                  ? `${(row.liquidity_pct_rank * 100).toFixed(1)}%`
                                  : "—"}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <WeeklyTrendChart data={weeklyTrendRows} isMobile={isMobile} />
          </>
        )}
      </div>
    </div>
  );
}

// function HeroMetric({ label, value, valueStyle = {} }) {
//   return (
//     <div style={heroMetricCard}>
//       <div style={heroMetricLabel}>{label}</div>
//       <div style={{ ...heroMetricValue, ...valueStyle }}>{value}</div>
//     </div>
//   );
// }

function StockMoveInline({ stock }) {
  if (!stock) {
    return <span style={{ color: "#94a3b8", fontWeight: 800 }}>—</span>;
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
      <span style={{ color: "#0f172a", fontWeight: 950 }}>{stock.ticker}</span>
      <ReturnPill value={stock.dailyReturn} />
    </div>
  );
}

const pageShell = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  padding: 16,
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  color: "#0f172a",
};

const heroCard = {
  borderRadius: 28,
  padding: 24,
  background:
    "radial-gradient(circle at 15% 10%, rgba(59,130,246,0.26), transparent 32%), linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
  color: "white",
  boxShadow: "0 24px 60px rgba(15,23,42,0.22)",
  marginBottom: 18,
  overflow: "hidden",
};

const eyebrow = {
  fontSize: 10,
  fontWeight: 900,
  color: "#93c5fd",
  letterSpacing: "0.13em",
  textTransform: "uppercase",
  marginBottom: 8,
};

const heroTitle = {
  margin: 0,
  fontSize: 22,
  lineHeight: 1,
  letterSpacing: "-0.06em",
};

const heroSubtitle = {
  margin: "10px 0 0",
  maxWidth: 980,
  color: "#cbd5e1",
  fontSize: 14,
  lineHeight: 1.65,
};

const heroStatsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 10,
  marginTop: 20,
};

// const heroMetricCard = {
//   padding: 14,
//   borderRadius: 18,
//   background: "rgba(255,255,255,0.1)",
//   border: "1px solid rgba(255,255,255,0.16)",
//   boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
// };

// const heroMetricLabel = {
//   fontSize: 10,
//   letterSpacing: "0.08em",
//   textTransform: "uppercase",
//   color: "#cbd5e1",
//   fontWeight: 850,
//   marginBottom: 7,
// };

// const heroMetricValue = {
//   fontSize: 18,
//   fontWeight: 950,
//   letterSpacing: "-0.035em",
//   color: "white",
// };

// const moversGrid = {
//   display: "grid",
//   gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
//   gap: 14,
//   marginBottom: 18,
// };

const controlsCard = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 14,
  flexWrap: "wrap",
  padding: 16,
  borderRadius: 22,
  border: "1px solid #e2e8f0",
  background: "rgba(255,255,255,0.9)",
  boxShadow: "0 14px 32px rgba(15,23,42,0.08)",
  marginBottom: 18,
};

const controlsRight = {
  display: "flex",
  gap: 10,
  alignItems: "center",
  flexWrap: "wrap",
};

const searchInput = {
  padding: "8px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  minWidth: 180,
  fontSize: 12,
  outline: "none",
  background: "white",
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const toggleLabel = {
  display: "flex",
  gap: 5,
  alignItems: "center",
  fontSize: 11,
  color: "#334155",
  fontWeight: 800,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 999,
  padding: "7px 9px",
};

const resultPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  background: "#eff6ff",
  color: "#1d4ed8",
  border: "1px solid #bfdbfe",
  borderRadius: 999,
  padding: "9px 12px",
  fontSize: 12,
  fontWeight: 850,
};

const chartHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  marginBottom: 14,
};

const tableCard = {
  border: "1px solid #e2e8f0",
  borderRadius: 24,
  background: "white",
  padding: 18,
  boxShadow: "0 18px 44px rgba(15,23,42,0.11)",
  overflow: "hidden",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  minWidth: 1980,
  tableLayout: "fixed",
};

const thStyle = {
  textAlign: "left",
  padding: "7px 7px",
  fontSize: 9,
  color: "#475569",
  borderBottom: "1px solid #e2e8f0",
  whiteSpace: "nowrap",
  background: "#f8fafc",
  position: "sticky",
  top: 0,
  zIndex: 2,
  fontWeight: 950,
  textTransform: "uppercase",
  letterSpacing: "0.035em",
};

const tdStyle = {
  padding: "7px 7px",
  borderBottom: "1px solid #eef2f7",
  fontSize: 11,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const stickyThStyle = {
  left: 0,
  zIndex: 6,
  minWidth: 92,
  width: 92,
  maxWidth: 92,
  background: "#f8fafc",
  boxShadow: "2px 0 0 rgba(148,163,184,0.18)",
};

const stickyTdStyle = {
  position: "sticky",
  left: 0,
  zIndex: 4,
  minWidth: 92,
  width: 92,
  maxWidth: 92,
  background: "#ffffff",
  boxShadow: "2px 0 0 rgba(226,232,240,0.85)",
  fontWeight: 900,
  color: "#0f172a",
};

const returnPill = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 58,
  padding: "4px 6px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 950,
  border: "1px solid transparent",
};

const positiveReturnPill = {
  color: "#15803d",
  background: "#ecfdf5",
  borderColor: "#bbf7d0",
};

const negativeReturnPill = {
  color: "#b91c1c",
  background: "#fff1f2",
  borderColor: "#fecdd3",
};

const neutralReturnPill = {
  color: "#64748b",
  background: "#f8fafc",
  borderColor: "#e2e8f0",
};

const messageCard = {
  padding: 18,
  borderRadius: 18,
  background: "white",
  border: "1px solid #e2e8f0",
  color: "#64748b",
  boxShadow: "0 12px 26px rgba(15,23,42,0.06)",
};

const errorCard = {
  padding: 16,
  background: "#fff1f2",
  color: "#9f1239",
  border: "1px solid #fecdd3",
  borderRadius: 16,
  whiteSpace: "pre-wrap",
};
